import React from 'react';

import QuestionsSection from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/EditPhoneSurveyConfigDialog/components/QuestionsSection';
import { FORM_IDS } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/constants';
import { emptyVoiceConfig } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/helpers';
import VoiceMessageConfigurationSection from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/VoiceMessageConfigurationSection/VoiceMessageConfigurationSection';
import type { PhoneSurveyConfigurationModel } from '~/server_shared/openapi/data-contracts';

interface AllowSavingState {
  welcome: boolean;
  response_line: boolean;
  illegal: boolean;
  ending: boolean;
  questions: boolean[];
}

interface CreateSectionsProps {
  configuration?: PhoneSurveyConfigurationModel;
  values: PhoneSurveyConfigurationModel;
  setFieldValue: (field: string, value: unknown) => void;
  setAllowSavingWithoutNewFiles: React.Dispatch<React.SetStateAction<AllowSavingState>>;
}

export const createSections = ({
  configuration,
  values,
  setFieldValue,
  setAllowSavingWithoutNewFiles,
}: CreateSectionsProps) => [
  {
    id: 'welcome',
    title: 'Welcome Message',
    node: (
      <div className="mb-20">
        <VoiceMessageConfigurationSection
          title="Set a welcome message"
          subtitle="This message will be played at the beginning of the survey."
          voiceMessageConfiguration={values?.welcome_message_configuration || emptyVoiceConfig()}
          radioButtonGroupId={`${FORM_IDS.WELCOME_MESSAGE_CONFIGURATION}.configuration_type`}
          textAreaId={`${FORM_IDS.WELCOME_MESSAGE_CONFIGURATION}.message_text`}
          voiceFileInputId={`${FORM_IDS.WELCOME_MESSAGE_CONFIGURATION}.file`}
          onReplaceExistingRecording={() => {
            setAllowSavingWithoutNewFiles((prev) => ({ ...prev, welcome: false }));
            setFieldValue(`${FORM_IDS.WELCOME_MESSAGE_CONFIGURATION}.permanent_audio_file_url`, undefined);
            setFieldValue(`${FORM_IDS.WELCOME_MESSAGE_CONFIGURATION}.stored_file_id`, undefined);
          }}
          storedFileIdPath={`${FORM_IDS.WELCOME_MESSAGE_CONFIGURATION}.stored_file_id`}
        />
      </div>
    ),
  },
  {
    id: 'questions',
    title: 'Survey Questions',
    node: (
      <QuestionsSection
        values={values}
        configuration={configuration}
        setFieldValue={setFieldValue}
        setAllowSavingWithoutNewFiles={setAllowSavingWithoutNewFiles}
      />
    ),
  },
  {
    id: 'response-line',
    title: 'Response Line Message',
    node: (
      <div className="mb-20">
        <VoiceMessageConfigurationSection
          title="Set a response line message"
          subtitle="This message will be played when the user enters a valid option."
          voiceMessageConfiguration={values?.response_line_configuration || emptyVoiceConfig()}
          radioButtonGroupId={`${FORM_IDS.RESPONSE_LINE_CONFIGURATION}.configuration_type`}
          textAreaId={`${FORM_IDS.RESPONSE_LINE_CONFIGURATION}.message_text`}
          voiceFileInputId={`${FORM_IDS.RESPONSE_LINE_CONFIGURATION}.file`}
          onReplaceExistingRecording={() => {
            setFieldValue(`${FORM_IDS.RESPONSE_LINE_CONFIGURATION}.stored_file_id`, undefined);
            setFieldValue(`${FORM_IDS.RESPONSE_LINE_CONFIGURATION}.permanent_audio_file_url`, undefined);
            setAllowSavingWithoutNewFiles((prev) => ({ ...prev, response_line: false }));
          }}
          storedFileIdPath={`${FORM_IDS.RESPONSE_LINE_CONFIGURATION}.stored_file_id`}
        />
      </div>
    ),
  },
  {
    id: 'illegal-response',
    title: 'Illegal Response Message',
    node: (
      <div className="mb-20">
        <VoiceMessageConfigurationSection
          title="Set an illegal response message"
          subtitle="This message will be played when the user enters an invalid option."
          voiceMessageConfiguration={values?.illegal_response_text_configuration || emptyVoiceConfig()}
          radioButtonGroupId={`${FORM_IDS.ILLEGAL_RESPONSE_TEXT_CONFIGURATION}.configuration_type`}
          textAreaId={`${FORM_IDS.ILLEGAL_RESPONSE_TEXT_CONFIGURATION}.message_text`}
          voiceFileInputId={`${FORM_IDS.ILLEGAL_RESPONSE_TEXT_CONFIGURATION}.file`}
          onReplaceExistingRecording={() => {
            setFieldValue(`${FORM_IDS.ILLEGAL_RESPONSE_TEXT_CONFIGURATION}.stored_file_id`, undefined);
            setFieldValue(`${FORM_IDS.ILLEGAL_RESPONSE_TEXT_CONFIGURATION}.permanent_audio_file_url`, undefined);
            setAllowSavingWithoutNewFiles((prev) => ({ ...prev, illegal: false }));
          }}
          storedFileIdPath={`${FORM_IDS.ILLEGAL_RESPONSE_TEXT_CONFIGURATION}.stored_file_id`}
        />
      </div>
    ),
  },
  {
    id: 'ending',
    title: 'Ending Message',
    node: (
      <div className="mb-20">
        <VoiceMessageConfigurationSection
          title="Set an ending message"
          subtitle="This message will be played at the end of the survey."
          voiceMessageConfiguration={values?.ending_message_configuration || emptyVoiceConfig()}
          radioButtonGroupId={`${FORM_IDS.ENDING_MESSAGE_CONFIGURATION}.configuration_type`}
          textAreaId={`${FORM_IDS.ENDING_MESSAGE_CONFIGURATION}.message_text`}
          voiceFileInputId={`${FORM_IDS.ENDING_MESSAGE_CONFIGURATION}.file`}
          onReplaceExistingRecording={() => {
            setFieldValue(`${FORM_IDS.ENDING_MESSAGE_CONFIGURATION}.stored_file_id`, undefined);
            setFieldValue(`${FORM_IDS.ENDING_MESSAGE_CONFIGURATION}.permanent_audio_file_url`, undefined);
            setAllowSavingWithoutNewFiles((prev) => ({ ...prev, ending: false }));
          }}
          storedFileIdPath={`${FORM_IDS.ENDING_MESSAGE_CONFIGURATION}.stored_file_id`}
        />
      </div>
    ),
  },
];
